import { ShipmentFieldComparison } from "./components/shipment-field-comparison";
import { Button, Checkbox } from "@heroui/react";
import { PayOrderDialog } from "./pay-order-dialog";
import { useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import {
  useAcceptAddressChange,
  type AddressChangeProposal,
  type ShipmentAddressCandidate,
} from "@/api/use-shipment-change-request";
const promiseTime = (
  value: string,
  precision: "DATE" | "DATETIME",
  timeZone: string,
  locale = "en"
) =>
  precision === "DATE"
    ? new Date(value).toLocaleDateString(locale, {
        timeZone: "UTC",
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : new Date(value).toLocaleString(locale, {
        timeZone,
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        timeZoneName: "short",
      });
function deliveryRange(
  offer: NonNullable<AddressChangeProposal["offer"]>,
  locale: string
) {
  const timeZone = offer.deliveredPrecision === "DATE" ? "UTC" : offer.timeZone;
  const date = new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone,
  });
  const from = new Date(offer.deliveredFastest),
    to = new Date(offer.deliveredSlowest);
  const firstDay = date.format(from),
    lastDay = date.format(to);
  if (offer.deliveredPrecision === "DATE")
    return firstDay === lastDay ? firstDay : `${firstDay} – ${lastDay}`;
  const time = new Intl.DateTimeFormat(locale, {
    hour: "numeric",
    minute: "2-digit",
    timeZone,
  });
  const end = new Intl.DateTimeFormat(locale, {
    hour: "numeric",
    minute: "2-digit",
    timeZone,
    timeZoneName: "short",
  });
  return `${firstDay}, ${time.format(from)} – ${firstDay === lastDay ? "" : `${lastDay}, `}${end.format(to)}`;
}
const addressText = (value: ShipmentAddressCandidate | null) =>
  value
    ? [
        value.line1,
        value.line2,
        value.city,
        value.province,
        value.postalCode,
        value.country,
      ]
        .filter(Boolean)
        .join(", ")
    : "—";

export function AddressChangeProposalDecision({
  shipmentId,
  orderId,
  requestId,
  proposal,
}: {
  shipmentId: string;
  orderId: string;
  requestId: string;
  proposal: AddressChangeProposal;
}) {
  const { t, i18n } = useTranslation("app");
  const accept = useAcceptAddressChange();
  const [payOpen, setPayOpen] = useState(false);
  const [acceptedFingerprint, setAcceptedFingerprint] = useState<string | null>(
    null
  );
  const pending = proposal.state === "WAITING_CUSTOMER";
  const pickupQuote = proposal.offer?.pickupPackageQuote;
  const pickupMoney = (cents: number) =>
    new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: "CAD",
    }).format(cents / 100);
  const money =
    proposal.commercialDeltaCents == null
      ? null
      : new Intl.NumberFormat("en-CA", {
          style: "currency",
          currency: "CAD",
        }).format(Math.abs(proposal.commercialDeltaCents) / 100);
  const expired =
    proposal.offer &&
    new Date(proposal.offer.expiresAt).getTime() <= Date.now();
  return (
    <div className="mt-2 space-y-5 rounded-lg border border-default-200 bg-background p-4 text-sm">
      <p className="font-medium">
        {t(`shipmentChangeRequest.flow.states.${proposal.state}`, {
          defaultValue: proposal.state.split("_").join(" "),
        })}
      </p>
      {proposal.packageCandidate && (
        <section className="space-y-2 rounded border p-3">
          <h3 className="font-semibold">
            {t("shipmentChangeRequest.flow.proposedPackages")}
          </h3>
          <p>{t("shipmentChangeRequest.flow.packageListReplaces")}</p>
          {proposal.packageCandidate.map((piece) => (
            <div
              key={piece.id}
              className="rounded-lg border border-default-200 p-3"
            >
              <p className="font-medium">
                {t("shipmentChangeRequest.flow.packageLabel", {
                  number: piece.index,
                })}
              </p>
              <p>
                {t("shipmentChangeRequest.flow.packageMeasurements", {
                  weight: piece.weightGrams / 1000,
                  length: Number(piece.lengthCm),
                  width: Number(piece.widthCm),
                  height: Number(piece.heightCm),
                })}
              </p>
              {piece.description && <p className="mt-1">{piece.description}</p>}
              <details className="mt-2 text-xs text-default-500">
                <summary className="cursor-pointer">
                  {t("shipmentChangeRequest.flow.packageDetails")}
                </summary>
                <dl
                  key={piece.id}
                  className="grid gap-1 border-t py-2 sm:grid-cols-2"
                >
                  {(
                    [
                      "id",
                      "index",
                      "kind",
                      "packageTypeId",
                      "weightGrams",
                      "lengthCm",
                      "widthCm",
                      "heightCm",
                      "weightOriginal",
                      "weightOriginalUnit",
                      "lengthOriginal",
                      "widthOriginal",
                      "heightOriginal",
                      "dimensionOriginalUnit",
                      "declaredValueCents",
                      "isFragile",
                      "requiresCooling",
                    ] as const
                  ).map((key) => (
                    <div key={key}>
                      <dt className="font-medium">
                        {t(`shipmentHistory.fields.${key}`)}
                      </dt>
                      <dd>
                        {piece[key] == null
                          ? t("shipmentChangeRequest.flow.packageNotSpecified")
                          : typeof piece[key] === "boolean"
                            ? piece[key]
                              ? t("shipmentHistory.yes")
                              : t("shipmentHistory.no")
                            : String(piece[key])}
                      </dd>
                    </div>
                  ))}
                </dl>
              </details>
            </div>
          ))}
        </section>
      )}
      {proposal.changes.map((side) => {
        const fields = [
          "line1",
          "line2",
          "city",
          "province",
          "postalCode",
          "country",
        ] as const;
        const changedFields = fields.filter(
          (field) => side.before?.[field] !== side.after[field]
        );
        const rows = (changedFields.length ? changedFields : fields).map(
          (field) => ({
            key: field,
            label: t(`shipmentHistory.fields.${field}`),
            changed:
              side.before != null && side.before[field] !== side.after[field],
            before:
              side.before == null
                ? t("shipmentHistory.notRecorded")
                : side.before[field] || t("shipmentHistory.clear"),
            after: side.after[field] || t("shipmentHistory.clear"),
          })
        );
        return (
          <section key={side.addressType} className="space-y-2">
            <h3 className="font-semibold">
              {t(
                side.addressType === "FROM"
                  ? "shipmentChangeRequest.flow.senderAddress"
                  : "shipmentChangeRequest.flow.recipientAddress"
              )}
            </h3>
            <ShipmentFieldComparison
              rows={rows}
              fieldLabel={t("shipmentHistory.field")}
              beforeLabel={t("shipmentChangeRequest.flow.before")}
              afterLabel={t("shipmentChangeRequest.flow.after")}
            />
            <p className="text-xs text-default-500">
              {addressText(side.after)}
            </p>
          </section>
        );
      })}
      {proposal.parties && proposal.parties.length > 0 && (
        <section className="grid gap-3 sm:grid-cols-2">
          {proposal.parties.map((party) => (
            <div
              key={party.type}
              className="space-y-1 rounded-lg bg-default-50 p-3"
            >
              <h3 className="text-xs font-medium text-default-500">
                {t(
                  party.type === "FROM"
                    ? "shipmentChangeRequest.flow.sender"
                    : "shipmentChangeRequest.flow.recipient"
                )}
              </h3>
              {(party.name || party.company) && (
                <p className="font-medium">
                  {[party.name, party.company].filter(Boolean).join(" · ")}
                </p>
              )}
              {party.phone && <p>{party.phone}</p>}
              {party.email && (
                <p className="break-all text-default-500">{party.email}</p>
              )}
              {party.instructions && (
                <p className="whitespace-pre-wrap pt-1">{party.instructions}</p>
              )}
            </div>
          ))}
        </section>
      )}
      {proposal.offer && (
        <section className="space-y-3 rounded-lg bg-default-50 p-3">
          <h3 className="font-semibold">
            {proposal.offer.service.name}
            <span className="font-normal text-default-500">
              {" "}
              · {proposal.offer.currency}
            </span>
          </h3>
          <dl className="space-y-3">
            <div>
              <dt className="text-xs text-default-500">
                {t("shipmentChangeRequest.flow.delivery")}
              </dt>
              <dd>{deliveryRange(proposal.offer, i18n?.language ?? "en")}</dd>
            </div>
            {proposal.offer.readyAt && (
              <div>
                <dt className="text-xs text-default-500">
                  {t("shipmentChangeRequest.flow.readyAt")}
                </dt>
                <dd>
                  {promiseTime(
                    proposal.offer.readyAt,
                    "DATETIME",
                    proposal.offer.timeZone,
                    i18n?.language
                  )}
                </dd>
              </div>
            )}
            {proposal.offer.pickupDate && (
              <div>
                <dt className="text-xs text-default-500">
                  {t("shipmentChangeRequest.flow.pickupDate")}
                </dt>
                <dd>
                  {promiseTime(
                    proposal.offer.pickupDate,
                    "DATE",
                    proposal.offer.timeZone,
                    i18n?.language
                  )}
                </dd>
              </div>
            )}
            {proposal.offer.handoffBy && (
              <div>
                <dt className="text-xs text-default-500">
                  {t("shipmentChangeRequest.flow.handoffBy")}
                </dt>
                <dd>
                  {promiseTime(
                    proposal.offer.handoffBy,
                    "DATETIME",
                    proposal.offer.timeZone,
                    i18n?.language
                  )}
                </dd>
              </div>
            )}
            {proposal.offer.dispatch && (
              <div>
                <dt className="text-xs text-default-500">
                  {t("shipmentChangeRequest.flow.dispatch")}
                </dt>
                <dd>{addressText(proposal.offer.dispatch)}</dd>
              </div>
            )}
            {proposal.offer.dropoff && (
              <div>
                <dt className="text-xs text-default-500">
                  {t("shipmentChangeRequest.flow.dropoff")}
                </dt>
                <dd>{addressText(proposal.offer.dropoff)}</dd>
              </div>
            )}
          </dl>
        </section>
      )}
      {proposal.offer?.pickupVisit && (
        <div className="rounded border p-3">
          <strong>{t("shipmentChangeRequest.flow.actualPickup")}</strong>
          <p>
            {t("shipmentChangeRequest.flow.before")}:{" "}
            {addressText(proposal.offer.pickupVisit.before)}
          </p>
          <p>
            {t("shipmentChangeRequest.flow.after")}:{" "}
            {addressText(proposal.offer.pickupVisit.after)}
          </p>
          <p>
            {proposal.offer.pickupVisit.service.name} —{" "}
            {proposal.offer.pickupVisit.scheduledDate.slice(0, 10)} —{" "}
            {proposal.offer.pickupVisit.slot.name}{" "}
            {proposal.offer.pickupVisit.slot.startTime}–
            {proposal.offer.pickupVisit.slot.endTime}
          </p>
          <p>
            {[
              proposal.offer.pickupVisit.contactName,
              proposal.offer.pickupVisit.contactPhone,
              proposal.offer.pickupVisit.contactEmail,
            ]
              .filter(Boolean)
              .join(" · ")}
          </p>
          <p>{proposal.offer.pickupVisit.instructions}</p>
          <p>
            {t("shipmentChangeRequest.flow.pickupPrice", {
              before: (proposal.offer.pickupVisit.oldGrossCents / 100).toFixed(
                2
              ),
              after: (proposal.offer.pickupVisit.newGrossCents / 100).toFixed(
                2
              ),
            })}
          </p>
        </div>
      )}
      {pickupQuote && (
        <div className="rounded border p-3 space-y-1">
          <strong>
            {t("shipmentChangeRequest.flow.reviewedPickup")} (
            {pickupQuote.currency})
          </strong>
          <p>
            {t("shipmentChangeRequest.flow.previousPickup")}:{" "}
            {t("shipmentChangeRequest.flow.pickupNet")}{" "}
            {pickupMoney(pickupQuote.oldNetCents)} +{" "}
            {t("shipmentChangeRequest.flow.pickupTax")}{" "}
            {pickupMoney(pickupQuote.oldTaxCents)}
          </p>
          <p>
            {t("shipmentChangeRequest.flow.proposedPickup")}:{" "}
            {t("shipmentChangeRequest.flow.pickupNet")}{" "}
            {pickupMoney(pickupQuote.newNetCents)} +{" "}
            {t("shipmentChangeRequest.flow.pickupTax")}{" "}
            {pickupMoney(pickupQuote.newTaxCents)}
          </p>
          <p>
            {t("shipmentChangeRequest.flow.pickupDifference")}:{" "}
            {pickupMoney(pickupQuote.deltaCents)}
          </p>
          <p>{t("shipmentChangeRequest.flow.pickupIncluded")}</p>
        </div>
      )}
      <section className="space-y-2 rounded-lg bg-default-50 p-3">
        <h3 className="text-xs font-medium text-default-500">
          {t("shipmentChangeRequest.flow.priceChange", { currency: "CAD" })}
        </h3>
        {!money && <p>{t("shipmentChangeRequest.flow.priceNotReviewed")}</p>}
        {money && (
          <p className="font-semibold">
            {t(
              (proposal.commercialDeltaCents ?? 0) < 0
                ? "shipmentChangeRequest.flow.refund"
                : (proposal.commercialDeltaCents ?? 0) > 0
                  ? "shipmentChangeRequest.flow.charge"
                  : "shipmentChangeRequest.flow.noCharge",
              { amount: money }
            )}
          </p>
        )}
        {proposal.commercialTaxCents != null && (
          <p>
            {t("shipmentChangeRequest.flow.taxIncluded", {
              amount: new Intl.NumberFormat("en-CA", {
                style: "currency",
                currency: "CAD",
              }).format(proposal.commercialTaxCents / 100),
            })}
          </p>
        )}
        {proposal.commercialReason && <p>{proposal.commercialReason}</p>}
      </section>
      {pending && (
        <>
          <Checkbox
            isSelected={acceptedFingerprint === proposal.fingerprint}
            onValueChange={(checked) =>
              setAcceptedFingerprint(checked ? proposal.fingerprint : null)
            }
          >
            {t("shipmentChangeRequest.flow.consent")}
          </Checkbox>
          <Button
            type="button"
            className="rounded bg-primary px-3 py-2 text-primary-foreground disabled:opacity-40"
            isDisabled={
              accept.isPending ||
              acceptedFingerprint !== proposal.fingerprint ||
              Boolean(expired) ||
              money == null
            }
            onPress={() =>
              accept.mutate({
                shipmentId,
                requestId,
                fingerprint: proposal.fingerprint,
              })
            }
          >
            {t("shipmentChangeRequest.flow.accept")}
          </Button>
          {expired && (
            <p role="alert">{t("shipmentChangeRequest.flow.expired")}</p>
          )}
        </>
      )}
      {proposal.state === "WAITING_FUNDING" && (
        <p>
          {t(
            (proposal.commercialDeltaCents ?? 0) > 0
              ? "shipmentChangeRequest.flow.payOrder"
              : "shipmentChangeRequest.flow.waitRefund"
          )}
        </p>
      )}
      {proposal.state === "WAITING_FUNDING" &&
        typeof proposal.commercialDeltaCents === "number" &&
        proposal.commercialDeltaCents > 0 && (
          <>
            <Button
              type="button"
              className="rounded border border-primary px-3 py-2"
              onPress={() => setPayOpen(true)}
            >
              {t("shipmentChangeRequest.flow.payAdjustment")}
            </Button>
            <PayOrderDialog
              open={payOpen}
              onOpenChange={setPayOpen}
              orderId={orderId}
              totalCents={proposal.commercialDeltaCents}
              purpose="ADDRESS_CHANGE"
              addressChangeShipmentId={shipmentId}
              addressChangeRequestId={requestId}
              allowInvoice={false}
            />
          </>
        )}
      {accept.error && (
        <p role="alert" className="text-danger">
          {axios.isAxiosError<{ message?: string }>(accept.error)
            ? (accept.error.response?.data.message ??
              t("shipmentChangeRequest.flow.failed"))
            : t("shipmentChangeRequest.flow.failed")}
        </p>
      )}
    </div>
  );
}
